import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, shopApi, formatApiError, getShopSession } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, UserCheck, Lock, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * Attendance panel on the shop landing page. Owner/manager can tick each
 * staff working name to mark who is on shift today; staff (and impersonating
 * super admin) see the same list read-only.
 *
 * State is per (shop, date) in the `staff_attendance` collection. Backed by
 * `GET /attendance` + `PUT /attendance`.
 */
export function TodayAttendance({ slug, shop }) {
  const { t } = useLang();
  const session = getShopSession(slug);
  const canEdit = ["owner", "manager"].includes(session?.role || "");
  // Default to the shop's business day (overnight-aware) when available so
  // early-morning hours still show yesterday's shift. Falls back to BKK calendar.
  const defaultDate = shop?.business_date_today
    || new Date(Date.now() + 420 * 60000).toISOString().slice(0, 10);
  const [date, setDate] = useState(defaultDate);
  // Keep the picker in sync once the shop payload lands.
  useEffect(() => {
    if (shop?.business_date_today) setDate(shop.business_date_today);
  }, [shop?.business_date_today]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      // Signed-in users hit the shop-session endpoint (same payload); anonymous
      // visitors on the landing page hit the public read-only endpoint so the
      // roster is always visible even before a PIN is entered.
      const r = session?.token
        ? await shopApi(slug).get(`/shops/${slug}/attendance`, { params: { date } })
        : await axios.get(`${API}/shops/by-slug/${slug}/attendance`, { params: { date } });
      setStaff(r.data.staff);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("attendance.load_failed"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, date]);

  const toggle = async (row) => {
    if (!canEdit || saving) return;
    const next = staff.map(s => s.id === row.id ? { ...s, is_working: !s.is_working } : s);
    setStaff(next); // optimistic
    setSaving(true);
    try {
      await shopApi(slug).put(`/shops/${slug}/attendance`, {
        working_staff_ids: next.filter(s => s.is_working).map(s => s.id),
      }, { params: { date } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
      setStaff(staff); // rollback
    } finally { setSaving(false); }
  };

  // Always visible on the shop landing page — anonymous visitors see a
  // read-only roster, signed-in owners/managers can tick to edit.
  const workingCount = staff.filter(s => s.is_working).length;
  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <Card className="mt-8 border-stone-200/80" data-testid="today-attendance-panel">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded bg-emerald-800 text-amber-50 flex items-center justify-center">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <div className="font-serif text-xl text-stone-900">{t("attendance.title")}</div>
              <div className="text-sm text-stone-500" data-testid="attendance-date-label">{dateLabel}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {shop?.opening_time_24h && shop?.closing_time_24h && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-stone-100 border border-stone-200 px-2.5 py-1 text-xs text-stone-700"
                data-testid="attendance-hours-badge"
              >
                <Clock className="w-3 h-3" />
                <span>{shop.opening_time_24h} → {shop.closing_time_24h}</span>
              </span>
            )}
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm"
              data-testid="attendance-date-input"
            />
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-1 text-xs text-emerald-900">
              <UserCheck className="w-3 h-3" />
              <span data-testid="attendance-count">{workingCount}</span> working
            </span>
            {!canEdit && (
              <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-xs text-stone-500">
                <Lock className="w-3 h-3" /> read-only
              </span>
            )}
          </div>
        </div>

        {staff.length === 0 ? (
          <div className="py-6 text-center text-stone-500 text-sm">{t("attendance.no_staff")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="attendance-list">
            {staff.map(s => {
              const disabled = !canEdit || saving;
              return (
                <label
                  key={s.id}
                  htmlFor={`att-${s.id}`}
                  className={`flex items-center gap-3 rounded-md border px-3 py-2 transition ${
                    s.is_working ? "border-emerald-200 bg-emerald-50/50" : "border-stone-200 bg-white"
                  } ${disabled ? "cursor-default" : "cursor-pointer hover:border-emerald-300"}`}
                  data-testid={`attendance-row-${s.working_name}`}
                >
                  <Checkbox
                    id={`att-${s.id}`}
                    checked={s.is_working}
                    onCheckedChange={() => toggle(s)}
                    disabled={disabled}
                    data-testid={`attendance-checkbox-${s.working_name}`}
                    className="data-[state=checked]:bg-emerald-800 data-[state=checked]:border-emerald-800"
                  />
                  <span className={`text-sm ${s.is_working ? "text-emerald-900 font-medium" : "text-stone-700"}`}>
                    {s.working_name}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
